import { ConfirmationModalComponent } from 'commons/components';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { useTranslation } from 'core/i18n/i18n';
import { executeRequest } from 'core/utils/backend-service';
import { showSuccessNotification } from 'core/utils/notification-service';
import { useConfigurationList } from 'modules/settings/contexts/device-configuration/hooks/use-configuration-list.hook';
import { usePopupManager } from 'react-popup-manager';
import { useNavigate } from 'react-router-dom';
import { changeConfigurationInstanceToDefault } from '../service/settings-devices-configuration.service';
import { SettingsDevicesConfigurationCreateFromModal } from '../settings-devices-configuration-create-from-modal/settings-devices-configuration-create-from-modal';
import { DeviceConfigurationInfo } from '../settings-devices-configuration-list/settings-devices-configuration-list.model';

export const useDeviceConfigurationNavigation = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const popupManager = usePopupManager();

    const { refreshConfigurationList } = useConfigurationList();

    const { mutateAsync: setConfigurationAsDefault } = usePocMutation({
        mutationFn: (configId: string) => executeRequest(() => changeConfigurationInstanceToDefault(configId)),
    });

    const handleDeviceConfigSelected = (deviceConfig: DeviceConfigurationInfo) => {
        navigate(deviceConfig.id);
    };

    const handleOnCopyFrom = (deviceConfig: DeviceConfigurationInfo) => {
        popupManager.open(SettingsDevicesConfigurationCreateFromModal, {
            baseConfiguration: deviceConfig,
            onCopyNewConfiguration: (newConfigurationId: string) => {
                handleDeviceConfigSelected({
                    ...deviceConfig,
                    id: newConfigurationId,
                    name: `${deviceConfig.name} - Copy`,
                });
            },
        });
    };

    const handleSetAsDefaultClick = (deviceConfig: DeviceConfigurationInfo) => {
        popupManager.open(ConfirmationModalComponent, {
            title: t(
                'settings.devices.device-settings.device-config-list.action.setAsDefault.confirmation-modal.title'
            ),
            children: (
                <div style={{ display: 'flex', flexDirection: 'column', rowGap: '16px' }}>
                    <span>
                        {t(
                            'settings.devices.device-settings.device-config-list.action.setAsDefault.confirmation-modal.description'
                        )}
                    </span>
                </div>
            ),
            buttonText: t(
                'settings.devices.device-settings.device-config-list.action.setAsDefault.confirmation-modal.confirm'
            ),
            cancelButtonText: t(
                'settings.devices.device-settings.device-config-list.action.setAsDefault.confirmation-modal.discard'
            ),
            actionOnClick: () =>
                setConfigurationAsDefault(deviceConfig.id).then(() => {
                    showSuccessNotification(
                        t('settings.devices.device-settings.device-config-layout.action.save.success')
                    );
                    refreshConfigurationList();
                }),
            onClose: () => {},
        });
    };

    const navigateToConfiguration = (configurationId: string) => {
        navigate(configurationId);
    };

    return {
        handleDeviceConfigSelected,
        handleOnCopyFrom,
        handleSetAsDefaultClick,
        navigateToConfiguration,
    };
};
